import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './Auth';

import { 
  FaPuzzlePiece, FaArrowRight, FaCheckCircle, FaDownload, FaAmazon, FaBolt, 
  FaBorderAll, FaSearch, FaRoute, FaLayerGroup, FaSortNumericUp, FaEye, FaTimes, FaTrophy, FaBars, FaBookOpen
} from 'react-icons/fa';

import MazeBoard from './MazeBoard';
import SudokuBoard from './SudokuBoard';
import WordSearchBoard from './WordSearchBoard';
import NumberPyramidBoard from './NumberPyramidBoard';
import CrosswordBoard from './CrosswordBoard';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedGame, setSelectedGame] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleCta = () => {
    if (user) navigate('/dashboard');
    else navigate('/register');
  };

  const goChallenge = () => {
    navigate("/sudoku-challenge");
  };

  const gamesList = [
    { id: 'sudoku', title: 'Sudoku', desc: 'Fill the grid so each row, column and box contains numbers 1-9.', icon: FaBorderAll, color: '#0984e3', bg: '#f0f8ff', Component: SudokuBoard },
    { id: 'wordsearch', title: 'Word Search', desc: 'Find hidden words placed horizontally, vertically or diagonally.', icon: FaSearch, color: '#008f72', bg: '#e8f5e9', Component: WordSearchBoard },
    { id: 'maze', title: 'Maze', desc: 'Find the path from start to finish.', icon: FaRoute, color: '#d63031', bg: '#fff5f5', Component: MazeBoard },
    { id: 'pyramid', title: 'Number Pyramid', desc: 'Each number equals the sum of the two numbers below it.', icon: FaSortNumericUp, color: '#6c5ce7', bg: '#f3f0ff', Component: NumberPyramidBoard },
    { id: 'crossword', title: 'Crossword', desc: 'Solve clues and fill the crossword grid.', icon: FaLayerGroup, color: '#d35400', bg: '#fff9e6', Component: CrosswordBoard }
  ];

  // Logic to handle window resizing and close menu if switching to desktop
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) setIsMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div style={styles.container}>
      {/* Navbar */}
      <nav style={styles.nav} role="navigation" aria-label="Main menu">
        <div style={styles.navWrapper}>
            <div style={styles.logoContainer} onClick={() => navigate('/')} role="banner" aria-label="Puzzlify Home">
            <FaPuzzlePiece size={28} color="#008f72" aria-hidden="true" />
            <span style={styles.logoText}>Puzzlify<span style={{color:'#2d3436'}}>.net</span></span>
            </div>

            {/* ?? ?????? (????? ??? ?? ???????? ??? ??? CSS) */}
            <button 
            style={styles.menuIconToggle} 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={isMenuOpen}
            >
            {isMenuOpen ? <FaTimes size={26} /> : <FaBars size={26} />}
            </button>

            {/* ???????: ????? ????? ?? ???????? ??? ??? ??? ?????? ????? */}
            <div style={{
                ...styles.navLinks, 
                ...(isMenuOpen ? styles.navLinksMobileActive : styles.navLinksResponsive)
            }}>
                <button onClick={() => { window.location.href='/blog/all-articles.html'; setIsMenuOpen(false); }} style={styles.linkBtn}>
                    <FaBookOpen style={{marginRight:6}} aria-hidden="true"/> Blog
                </button>
                
                <button onClick={() => { goChallenge(); setIsMenuOpen(false); }} style={styles.challengeBtn}>
                    <FaTrophy style={{marginRight:8}} aria-hidden="true"/> Challenge
                </button>

                {!user && (
                    <button onClick={() => { navigate('/login'); setIsMenuOpen(false); }} style={styles.linkBtn}>Login</button>
                )}
                
                <button onClick={() => { handleCta(); setIsMenuOpen(false); }} style={styles.ctaBtn}>
                    {user ? "Dashboard" : "Get Started"} 
                    <FaArrowRight style={{marginLeft:8, fontSize: 14}} aria-hidden="true"/>
                </button>
            </div>
        </div>
      </nav>

      {/* Main Landmark */}
      <main id="main-content">
        {/* Hero Section */}
        <header style={styles.hero}>
          <div style={styles.heroContent}>
            <h1 style={styles.h1}>
              Create Professional <br/>
              <span style={{color:"#008f72"}}>Puzzle Books</span>
            </h1>
            <p style={styles.subtitle}>
              The #1 engine for Sudoku, Mazes, and Word Searches. <br/>
              Generate print-ready PDFs perfectly formatted for <b>Amazon KDP</b>.
            </p>

            <div style={styles.heroButtons}>
              <button onClick={handleCta} style={styles.primaryBtn} aria-label="Start creating puzzles for free">
                Start Creating Free
              </button>
              <button onClick={goChallenge} style={styles.challengeHeroBtn}>
                <FaTrophy style={{marginRight:8}} aria-hidden="true"/> Play Sudoku Challenge
              </button>
            </div>
          </div>
        </header>

        {/* Blog Strategy Section */}
        <section style={styles.sectionBlog} aria-labelledby="blog-heading">
          <div style={styles.wrapper}>
            <div style={styles.blogBox}>
              <div style={styles.blogTextSide}>
                <h2 id="blog-heading" style={{...styles.h2, textAlign: 'left', marginBottom: 15, fontSize: 32}}>
                    Become a Puzzle Master
                </h2>
                <p style={{...styles.cardP, fontSize: 18, marginBottom: 25, color: '#2d3436'}}>
                  Don't just play, understand the patterns. Discover expert strategies, solving techniques for seniors, 
                  and secrets to publishing successful puzzle books on Amazon KDP.
                </p>
                <button 
                  onClick={() => window.location.href='/blog/all-articles.html'} 
                  style={styles.blogBtn}
                >
                  Explore Strategy Guides <FaArrowRight style={{marginLeft:10}} aria-hidden="true"/>
                </button>
              </div>
              <div style={styles.blogIconSide} aria-hidden="true">
                 <FaBookOpen size={100} color="rgba(0,143,114,0.15)" />
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section style={styles.sectionGray} aria-labelledby="features-heading">
          <div style={styles.wrapper}>
            <h2 id="features-heading" style={styles.h2}>Why Creators Choose Puzzlify</h2>
            <div style={styles.featureGrid}>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}><FaBolt size={30} color="#008f72"/></div>
                <h3 style={styles.cardH3}>Instant Generation</h3>
                <p style={styles.cardP}>Generate hundreds of unique puzzles in seconds with our advanced algorithms.</p>
              </div>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}><FaDownload size={30} color="#0984e3"/></div>
                <h3 style={styles.cardH3}>PDF Export</h3>
                <p style={styles.cardP}>Download high-quality PDFs with customizable layouts and included solutions.</p>
              </div>
              <div style={styles.featureCard}>
                <div style={styles.featureIcon}><FaAmazon size={30} color="#d35400"/></div>
                <h3 style={styles.cardH3}>KDP Ready</h3>
                <p style={styles.cardP}>Standard bleed and margin settings pre-configured for Amazon publishing.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Puzzle Engine Section */}
        <section style={styles.sectionWhite} aria-labelledby="engine-heading">
          <div style={styles.wrapper}>
            <h2 id="engine-heading" style={styles.h2}>Our Puzzle Engine</h2>
            <div style={styles.gamesGrid}>
              {gamesList.map(game => (
                <div key={game.id} style={styles.gameCard}>
                  <div style={{...styles.iconBox, background:game.bg, color:game.color}}>
                    <game.icon size={32} aria-hidden="true" />
                  </div>
                  <h3 style={styles.cardH3}>{game.title}</h3>
                  <p style={styles.gameDesc}>{game.desc}</p>
                  <button
                    style={{...styles.viewExampleBtn, borderColor: game.color, color: game.color}}
                    onClick={()=>setSelectedGame(game)}
                    aria-label={`View demo for ${game.title}`}
                  >
                    <FaEye style={{marginRight:8}} aria-hidden="true"/> View Live Demo
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Modal */}
      {selectedGame && (
        <div 
          style={styles.modalOverlay} 
          onClick={()=>setSelectedGame(null)} 
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="modal-title"
        >
          <div style={styles.modalContent} onClick={(e)=>e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 id="modal-title" style={{fontSize: 22, fontWeight: 'bold'}}>{selectedGame.title} Demo</h3>
              <button style={styles.closeBtn} onClick={()=>setSelectedGame(null)} aria-label="Close modal">
                <FaTimes size={20}/>
              </button>
            </div>
            <div style={styles.modalBody}>
              <selectedGame.Component isPreview={true} initialSeed={12345} />
            </div>
            <div style={styles.modalFooter}>
              <button onClick={handleCta} style={styles.modalCta}>
                Create This Puzzle Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer style={styles.footer} role="contentinfo">
        <div style={styles.wrapper}>
          <p style={{fontWeight: 'bold', fontSize: 20, color: '#00b894'}}>Puzzlify.net</p>
          <p style={{opacity: 0.8, marginTop: 10}}>� 2026 Puzzlify. Built for puzzle lovers and KDP publishers.</p>
        </div>
      </footer>

      {/* CSS Injection ????? ??????? ???????? 100% */}
      <style>{`
        @media (max-width: 768px) {
            .nav-links-res { display: none !important; }
            .menu-toggle-btn { display: block !important; }
        }
        @media (min-width: 769px) {
            .menu-toggle-btn { display: none !important; }
            .nav-links-res { display: flex !important; }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: { fontFamily: "'Segoe UI', Roboto, sans-serif", color: "#2d3436", backgroundColor: "#fff", overflowX: 'hidden' },
  nav: {
    background: "#fff",
    boxShadow: "0 2px 15px rgba(0,0,0,0.05)", position: "sticky", top: 0, zIndex: 1000,
    width: '100%'
  },
  navWrapper: {
    maxWidth: 1100, margin: '0 auto', padding: "15px 20px",
    display: "flex", justifyContent: "space-between", alignItems: "center",
    position: 'relative'
  },
  logoContainer: { display: "flex", alignItems: "center", cursor: "pointer" },
  logoText: { fontWeight: "800", marginLeft: 10, fontSize: 24, color: "#008f72", letterSpacing: "-1px" },
  
  // Menu Toggle Button
  menuIconToggle: { 
    display: 'none', // Hidden by default, shown by CSS at <768px
    background: 'none', border: 'none', cursor: 'pointer', color: '#2d3436',
    className: 'menu-toggle-btn' 
  },

  // Navigation Links Container
  navLinks: { 
    display: "flex", gap: 15, alignItems: "center",
    transition: 'all 0.3s ease'
  },
  navLinksResponsive: { className: 'nav-links-res' },
  navLinksMobileActive: {
    display: 'flex', flexDirection: 'column', position: 'absolute', 
    top: '100%', left: 0, width: '100%', background: '#fff', 
    padding: '20px', boxShadow: '0 10px 15px rgba(0,0,0,0.1)', gap: '20px',
    zIndex: 999
  },

  linkBtn: { background: "none", border: "none", cursor: "pointer", fontWeight: "600", color: "#2d3436", fontSize: 16, display: 'flex', alignItems: 'center' },
  ctaBtn: {
    background: "#008f72", color: "white", border: "none", padding: "10px 22px",
    borderRadius: 50, cursor: "pointer", fontWeight: "bold", display: "flex", alignItems: "center"
  },
  challengeBtn: {
    background: "#f39c12", color: "#fff", border: "none", padding: "10px 22px",
    borderRadius: 50, cursor: "pointer", fontWeight: "bold", display: "flex", alignItems: "center"
  },

  hero: { textAlign: "center", padding: "80px 20px", background: "linear-gradient(180deg, #f0f8ff 0%, #ffffff 100%)" },
  h1: { fontSize: "clamp(32px, 5vw, 64px)", fontWeight: "800", marginBottom: 20, lineHeight: 1.1 },
  subtitle: { fontSize: 18, color: "#2d3436", maxWidth: 700, margin: "0 auto 40px", lineHeight: 1.6 },
  heroButtons: { display: "flex", gap: 15, justifyContent: "center", flexWrap: "wrap" },
  
  primaryBtn: { background: "#d63031", border: "none", padding: "14px 30px", color: "white", borderRadius: 50, cursor: "pointer", fontSize: 18, fontWeight: "bold" },
  challengeHeroBtn: { background: "#fff", border: "2px solid #f39c12", padding: "14px 30px", borderRadius: 50, cursor: "pointer", fontSize: 18, fontWeight: "bold", color: "#2d3436" },

  sectionBlog: { padding: "40px 0", background: "#fff" },
  blogBox: { 
    background: "#f8f9fa", borderRadius: 30, padding: 40, display: "flex", 
    alignItems: "center", justifyContent: "space-between", border: "1px solid #eee" 
  },
  blogTextSide: { flex: 1, paddingRight: 20 },
  blogIconSide: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  blogBtn: { 
    background: "transparent", border: "2px solid #008f72", color: "#008f72",
    padding: "12px 25px", borderRadius: 50, fontWeight: "bold", cursor: "pointer",
    display: "flex", alignItems: "center"
  },

  sectionGray: { background: "#f8f9fa", padding: "80px 0" },
  sectionWhite: { padding: "80px 0" },
  wrapper: { maxWidth: 1100, margin: "auto", padding: "0 20px" },
  h2: { textAlign: "center", fontSize: 32, fontWeight: "800", marginBottom: 40, color: "#2d3436" },
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 30 },
  featureCard: { background: "white", padding: 30, borderRadius: 20, textAlign: "center", boxShadow: "0 10px 30px rgba(0,0,0,0.05)" },
  featureIcon: { marginBottom: 20, background: "#f8f9fa", width: 70, height: 70, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%", margin: "0 auto 15px" },
  cardH3: { fontSize: 20, fontWeight: "700", marginBottom: 12 },
  cardP: { color: "#2d3436", lineHeight: 1.6 },

  gamesGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: 25 },
  gameCard: { background: "white", border: "1px solid #f1f2f6", padding: 25, borderRadius: 20, textAlign: "center" },
  iconBox: { width: 60, height: 60, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 15, margin: "0 auto 15px" },
  gameDesc: { color: "#2d3436", fontSize: 14, marginBottom: 15 },
  viewExampleBtn: { padding: "8px 16px", borderRadius: 10, background: "transparent", border: "2px solid", fontWeight: "bold", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", margin: "auto" },

  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 },
  modalContent: { background: "white", padding: 25, borderRadius: 20, maxWidth: "90%", position: "relative" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  closeBtn: { background: "none", border: "none", cursor: "pointer" },
  modalBody: { marginTop: 20 },
  modalFooter: { marginTop: 20, textAlign: "center" },
  modalCta: { background: "#008f72", color: "white", border: "none", padding: "12px 25px", borderRadius: 50, fontWeight: "bold", cursor: "pointer" },

  footer: { textAlign: "center", padding: "40px 20px", background: "#2d3436", color: "white" },
};
<script async src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3414154610532392"
     crossorigin="anonymous"></script>

export default LandingPage;